import { useEffect, useState } from "react";

/** Phase 2B — Reusable Furmais Genie loader.
 *
 * Drops in anywhere we want a calm, premium "something is happening"
 * surface. Renders the transparent Genie PNG + a soft glow + a rotating
 * status line. NEVER blocks the UI; the parent decides when to unmount.
 *
 *   <GenieLoader />                          // small inline
 *   <GenieLoader size="lg" />                // hero-scale
 *   <GenieLoader inline />                   // single-line variant
 *   <GenieLoader messages={["Custom…"]} />   // override copy
 *
 * Performance: the PNG is cached after first paint and the message rotation
 * is a single setInterval. No layout thrash.
 */
const DEFAULT_MESSAGES = [
    "Preparing your Furmais…",
    "Aligning strategy…",
    "Generating ideas…",
    "Building your output…",
    "Kuch jadu ho raha hai…",
    "Almost ready…",
];

export default function GenieLoader({
    size = "md",                  // sm | md | lg
    label,                        // override default rotating messages with a fixed label
    messages = DEFAULT_MESSAGES,
    inline = false,
    className = "",
}) {
    const [i, setI] = useState(0);
    useEffect(() => {
        if (label) return;
        const t = setInterval(() => setI(p => (p + 1) % messages.length), 2200);
        return () => clearInterval(t);
    }, [label, messages]);

    const dim = size === "lg" ? "w-32 h-32" : size === "sm" ? "w-10 h-10" : "w-20 h-20";

    if (inline) {
        return (
            <span className={`inline-flex items-center gap-2 text-zinc-300 ${className}`} data-testid="genie-loader-inline">
                <span className="relative inline-flex">
                    <span className="absolute inset-0 rounded-full bg-[#FF4400]/20 blur-md genie-pulse" aria-hidden="true" />
                    <img
                        src="/genie/genie.png"
                        alt="Furmais AI Genie mascot"
                        loading="lazy"
                        className="relative w-6 h-6 object-contain"
                    />
                </span>
                <span key={label || i} className="text-sm font-mono uppercase tracking-wider text-zinc-500 genie-fade">
                    {label || messages[i]}
                </span>
            </span>
        );
    }

    return (
        <div className={`flex flex-col items-center justify-center text-center py-10 ${className}`} data-testid="genie-loader">
            <div className="relative">
                <div className={`absolute inset-0 rounded-full bg-[#FF4400]/20 blur-2xl genie-pulse`} aria-hidden="true" />
                <img
                    src="/genie/genie.png"
                    alt="Furmais AI Genie mascot"
                    loading="lazy"
                    className={`relative ${dim} object-contain`}
                />
            </div>
            <div
                key={label || i}
                className="mt-5 text-sm font-mono uppercase tracking-wider text-zinc-400 genie-fade"
                data-testid="genie-loader-message"
            >
                {label || messages[i]}
            </div>
        </div>
    );
}
